import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Group,
  HoverCard,
  NumberInput,
  ScrollArea,
  Select,
  Stack,
  Text,
} from '@mantine/core'
import { showNotification } from '@mantine/notifications'
import { IconChartBar, IconExternalLink, IconRefresh } from '@/components/icons'
import {
  AppModal,
  DataTable,
  DataTableScroll,
  DataTbody,
  DataTd,
  DataTh,
  DataThead,
  DataTr,
  EmptyState,
  InlineLoader,
  PageCenterLoader,
  PageShell,
  PageToolbar,
} from '@/components/ui'
import { useAuth } from '@/lib/auth-context'
import { api, ApiError } from '@/lib/api'
import {
  mergeWithDefaultPermissions,
  hasPermission,
  loadMyPermissions,
  readCachedPermissions,
} from '@/lib/permissions'
import { formatDateTimeDot, ticketPathSegmentFromListItem, ticketPublicId } from '@/lib/ticket-display'
import { useWorkspaceTabs } from '@/lib/workspace-tabs-context'
import type {
  AnomalyTicketRow,
  AnomalyTicketsResponse,
  MyPermissionsResponse,
  ProjectRow,
  TicketAnomalyRow,
  TicketMessagePreviewRow,
} from '@/types/api'

const PAGE_SIZE = 25

function AnomalySubjectPreview({ ticketId }: { ticketId: string }) {
  const [loading, setLoading] = useState(true)
  const [messages, setMessages] = useState<TicketMessagePreviewRow[]>([])

  useEffect(() => {
    let alive = true
    setLoading(true)
    void api
      .ticketMessages(ticketId, { limit: 5, offset: 0 })
      .then((res) => {
        if (!alive) return
        setMessages(res.messages ?? [])
      })
      .catch(() => {
        if (alive) setMessages([])
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
    }
  }, [ticketId])

  if (loading) {
    return (
      <Group justify="center" p="md">
        <InlineLoader label="" />
      </Group>
    )
  }
  if (messages.length === 0) {
    return (
      <Text size="sm" c="dimmed" p="sm">
        Нет сообщений
      </Text>
    )
  }
  return (
    <Stack gap="xs" p="xs" maw={420}>
      <Text size="xs" fw={600} tt="uppercase" c="dimmed">
        Первые сообщения
      </Text>
      {messages.map((m) => {
        const isCustomer = m.message_type === 'customer'
        return (
          <Box
            key={m.id}
            p="xs"
            style={{
              borderRadius: 8,
              background: isCustomer ? 'var(--mantine-color-blue-light)' : 'var(--mantine-color-gray-light)',
            }}
          >
            <Group justify="space-between" gap="xs" wrap="nowrap" mb={4}>
              <Text size="xs" fw={600}>
                {isCustomer ? m.sender_name || 'Клиент' : m.sender_name || 'Оператор'}
              </Text>
              <Text size="xs" c="dimmed">
                {m.created_at ? formatDateTimeDot(m.created_at) : ''}
              </Text>
            </Group>
            <Text size="sm" lineClamp={4} c={m.content?.trim() ? undefined : 'dimmed'} fs={m.content?.trim() ? undefined : 'italic'}>
              {m.content?.trim()
                ? m.content!.length > 220
                  ? `${m.content!.slice(0, 220)}…`
                  : m.content
                : 'Нет текста'}
            </Text>
          </Box>
        )
      })}
    </Stack>
  )
}

export function AnomaliesPage() {
  const { user } = useAuth()
  const { openOrSelectTab, openNewTab } = useWorkspaceTabs()

  const [perm, setPerm] = useState<MyPermissionsResponse | null>(() => readCachedPermissions())
  useEffect(() => {
    void loadMyPermissions(user?.role).then(setPerm)
  }, [user?.role])
  const effectivePerm = mergeWithDefaultPermissions(perm, user?.role)
  const canAnomalies = hasPermission(effectivePerm, 'access_anomalies')

  const [projects, setProjects] = useState<ProjectRow[]>([])
  const [projectId, setProjectId] = useState('')
  const [days, setDays] = useState(1)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [rows, setRows] = useState<TicketAnomalyRow[]>([])

  const [ticketsModal, setTicketsModal] = useState<{
    open: boolean
    loading: boolean
    data: AnomalyTicketsResponse | null
  }>({ open: false, loading: false, data: null })

  useEffect(() => {
    let alive = true
    void api.projects().then((p) => {
      if (alive) setProjects(Array.isArray(p) ? p : [])
    })
    return () => {
      alive = false
    }
  }, [])

  const loadPage = useCallback(
    async (p: number) => {
      setLoading(true)
      setError(null)
      try {
        const list = await api.anomaliesList({
          project_id: projectId || undefined,
          days,
          limit: PAGE_SIZE,
          offset: (p - 1) * PAGE_SIZE,
        })
        setRows(Array.isArray(list) ? list : [])
      } catch (e) {
        setError(e instanceof ApiError ? e.message : 'Ошибка загрузки')
        setRows([])
      } finally {
        setLoading(false)
      }
    },
    [projectId, days],
  )

  useEffect(() => {
    void loadPage(page)
  }, [page, loadPage])

  const handleRefresh = () => {
    if (page !== 1) setPage(1)
    else void loadPage(1)
  }

  const projectNameById = useMemo(() => {
    const m = new Map<string, string>()
    for (const p of projects) m.set(p.id, p.name || p.slug || p.id)
    return m
  }, [projects])

  const openTickets = async (anomalyId: string) => {
    setTicketsModal({ open: true, loading: true, data: null })
    try {
      const data = await api.anomalyTickets(anomalyId)
      setTicketsModal({ open: true, loading: false, data })
      const tickets = data.tickets ?? []
      const ids = data.ticket_ids ?? []
      if (tickets.length === 0 && ids.length === 0) {
        showNotification({
          title: 'Нет данных',
          message: 'Для этой аномалии не найдено обращений в окне.',
          color: 'blue',
        })
      }
    } catch {
      setTicketsModal({ open: false, loading: false, data: null })
      showNotification({ title: 'Ошибка', message: 'Не удалось загрузить обращения', color: 'red' })
    }
  }

  const openTicketRow = (t: AnomalyTicketRow, projectSlug: string | null | undefined) => {
    const ref = ticketPathSegmentFromListItem({
      id: t.id,
      ticket_number: t.ticket_number,
      project_slug: projectSlug ?? undefined,
    })
    openOrSelectTab(`/tickets/${encodeURIComponent(ref)}`)
  }

  const openTicketRowInBackground = (t: AnomalyTicketRow, projectSlug: string | null | undefined) => {
    const ref = ticketPathSegmentFromListItem({
      id: t.id,
      ticket_number: t.ticket_number,
      project_slug: projectSlug ?? undefined,
    })
    openNewTab(`/tickets/${encodeURIComponent(ref)}`, { activate: false })
  }

  const renderTags = (a: TicketAnomalyRow) => {
    const tags = a.tags_snapshot?.tags
    if (!Array.isArray(tags) || tags.length === 0) {
      return (
        <Text size="sm" c="dimmed">
          —
        </Text>
      )
    }
    return (
      <Group gap={6} wrap="wrap">
        {tags.map((t, i) => {
          const color = t.color || undefined
          return (
            <Badge
              key={`${t.tag_id || t.name || i}`}
              size="sm"
              variant="light"
              color={color ? undefined : 'gray'}
              styles={
                color
                  ? {
                      root: {
                        background: `${color}22`,
                        borderColor: `${color}55`,
                        color: 'var(--mantine-color-text)',
                      },
                    }
                  : undefined
              }
            >
              {t.name || t.tag_id || '—'} ×{t.count ?? 0}
            </Badge>
          )
        })}
      </Group>
    )
  }

  const hasPrev = page > 1
  const hasNext = rows.length === PAGE_SIZE

  if (!canAnomalies) {
    return (
      <PageShell>
        <Alert color="gray" variant="light" title="Нет доступа">
          Недостаточно прав для раздела «Аномалии».
        </Alert>
      </PageShell>
    )
  }

  return (
    <PageShell>
      <Stack gap="md">
        <PageToolbar
          leading={
            <Box>
              <Group gap="xs" wrap="nowrap">
                <IconChartBar size={20} stroke={1.6} />
                <Text fw={700} size="lg">
                  Аномалии
                </Text>
              </Group>
              <Text size="sm" c="dimmed">
                Всплески обращений по проектам и меткам за выбранный период.
              </Text>
            </Box>
          }
        />

        <Card withBorder padding="md" radius="md">
          <PageToolbar
            leading={
              <>
                <Select
                  label="Проект"
                  data={[
                    { value: '', label: 'Все проекты' },
                    ...projects.map((p) => ({ value: p.id, label: p.name || p.slug || p.id })),
                  ]}
                  value={projectId}
                  onChange={(value) => {
                    setProjectId(value || '')
                    setPage(1)
                  }}
                  searchable
                  allowDeselect={false}
                  checkIconPosition="right"
                  styles={{ root: { minWidth: 260 } }}
                />
                <NumberInput
                  label="Период (дней)"
                  min={1}
                  max={30}
                  value={days}
                  onChange={(v) => {
                    setDays(typeof v === 'number' ? v : 1)
                    setPage(1)
                  }}
                  style={{ width: 132 }}
                />
              </>
            }
            trailing={
              <Button
                variant="default"
                leftSection={<IconRefresh size={16} stroke={1.5} />}
                loading={loading}
                onClick={() => handleRefresh()}
              >
                Обновить
              </Button>
            }
          />
        </Card>

        {error ? (
          <Alert color="red" variant="light" title="Ошибка">
            {error}
          </Alert>
        ) : null}

        {loading ? (
          <PageCenterLoader label="Загрузка" minHeight={200} />
        ) : rows.length === 0 ? (
          <EmptyState title="Аномалий нет" description="За выбранный период ничего не зафиксировано." />
        ) : (
          <Stack gap="sm">
            <Group justify="space-between" wrap="wrap">
              <Text size="sm" c="dimmed">
                Найдено на странице: {rows.length}
              </Text>
              <Text size="xs" c="dimmed">
                Рост считается относительно среднего потока в базовом окне.
              </Text>
            </Group>
            <DataTableScroll>
              <DataTable>
                <DataThead>
                  <DataTr>
                    <DataTh>Обнаружено</DataTh>
                    <DataTh>Проект</DataTh>
                    <DataTh>Окна (мин)</DataTh>
                    <DataTh>Сейчас</DataTh>
                    <DataTh>Средний поток</DataTh>
                    <DataTh>Рост</DataTh>
                    <DataTh>Топ меток</DataTh>
                    <DataTh style={{ width: 1 }} />
                  </DataTr>
                </DataThead>
                <DataTbody>
                  {rows.map((a) => {
                    const pname = a.project_id ? projectNameById.get(a.project_id) : null
                    const growth = a.growth_percent
                    const growthTone =
                      growth >= 100 ? 'red' : growth >= 50 ? 'orange' : 'gray'
                    return (
                      <DataTr key={a.id}>
                        <DataTd>
                          <Text size="sm" style={{ whiteSpace: 'nowrap' }}>
                            {formatDateTimeDot(a.detected_at)}
                          </Text>
                        </DataTd>
                        <DataTd>
                          <Text size="sm">{pname || a.project_id || '—'}</Text>
                        </DataTd>
                        <DataTd>
                          <Text size="sm" c="dimmed">
                            {a.eval_window_minutes} / {a.baseline_window_minutes}
                          </Text>
                        </DataTd>
                        <DataTd>
                          <Text size="sm" fw={600}>
                            {a.current_count}
                          </Text>
                        </DataTd>
                        <DataTd>
                          <Text size="sm" c="dimmed">
                            {Number.isFinite(a.avg_rate) ? a.avg_rate.toFixed(2) : '—'}
                          </Text>
                        </DataTd>
                        <DataTd>
                          <Badge variant="light" color={growthTone}>
                            {Number.isFinite(growth) ? `${growth.toFixed(1)}%` : '—'}
                          </Badge>
                        </DataTd>
                        <DataTd style={{ maxWidth: 280 }}>{renderTags(a)}</DataTd>
                        <DataTd>
                          <Button size="compact-xs" variant="light" onClick={() => void openTickets(a.id)}>
                            Обращения
                          </Button>
                        </DataTd>
                      </DataTr>
                    )
                  })}
                </DataTbody>
              </DataTable>
            </DataTableScroll>

            <Group justify="space-between" wrap="wrap">
              <Text size="xs" c="dimmed">
                Показаны {(page - 1) * PAGE_SIZE + 1}–{(page - 1) * PAGE_SIZE + rows.length}
              </Text>
              <Group gap="xs">
                <Button size="xs" variant="default" disabled={!hasPrev} onClick={() => setPage((p) => p - 1)}>
                  Назад
                </Button>
                <Text size="xs" c="dimmed">
                  Стр. {page}
                </Text>
                <Button size="xs" variant="default" disabled={!hasNext} onClick={() => setPage((p) => p + 1)}>
                  Вперёд
                </Button>
              </Group>
            </Group>
          </Stack>
        )}
      </Stack>

      <AppModal
        opened={ticketsModal.open}
        onClose={() => setTicketsModal({ open: false, loading: false, data: null })}
        title="Обращения из аномалии"
        size="xl"
      >
        {ticketsModal.loading ? (
          <PageCenterLoader label="Загрузка" minHeight={160} />
        ) : ticketsModal.data?.tickets && ticketsModal.data.tickets.length > 0 ? (
          <>
            <Text size="sm" c="dimmed" mb="sm">
              Всего: {ticketsModal.data.tickets.length}. Наведите на тему — краткий превью диалога. Клик по номеру
              откроет обращение.
            </Text>
            <ScrollArea.Autosize mah={440}>
              <DataTable>
                <DataThead>
                  <DataTr>
                    <DataTh style={{ width: 1 }}>#</DataTh>
                    <DataTh>Тема</DataTh>
                    <DataTh>Создано</DataTh>
                    <DataTh>Метки</DataTh>
                    <DataTh style={{ width: 1 }} />
                  </DataTr>
                </DataThead>
                <DataTbody>
                  {ticketsModal.data.tickets.map((t) => {
                    const slug = ticketsModal.data?.project_slug
                    const num = ticketPublicId(slug, t.ticket_number)
                    return (
                      <DataTr key={t.id}>
                        <DataTd>
                          <Text
                            size="sm"
                            fw={600}
                            style={{ cursor: 'pointer', textDecoration: 'underline dotted' }}
                            onClick={() => {
                              setTicketsModal({ open: false, loading: false, data: null })
                              openTicketRow(t, slug)
                            }}
                          >
                            {num}
                          </Text>
                        </DataTd>
                        <DataTd style={{ maxWidth: 360 }}>
                          <HoverCard width={440} shadow="md" withArrow openDelay={400} closeDelay={80}>
                            <HoverCard.Target>
                              <Text size="sm" lineClamp={2} style={{ cursor: 'help' }}>
                                {t.subject?.trim() || '—'}
                              </Text>
                            </HoverCard.Target>
                            <HoverCard.Dropdown p={0}>
                              <AnomalySubjectPreview ticketId={t.id} />
                            </HoverCard.Dropdown>
                          </HoverCard>
                        </DataTd>
                        <DataTd>
                          <Text size="sm" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                            {t.created_at ? formatDateTimeDot(t.created_at) : '—'}
                          </Text>
                        </DataTd>
                        <DataTd>
                          <Group gap={4} wrap="wrap">
                            {(t.tags ?? []).map((tag) => (
                              <Badge
                                key={tag.id}
                                size="xs"
                                variant="outline"
                                style={{
                                  borderColor: tag.color ? `${tag.color}88` : undefined,
                                }}
                              >
                                {tag.name}
                              </Badge>
                            ))}
                            {(t.tags ?? []).length === 0 ? (
                              <Text size="xs" c="dimmed">
                                —
                              </Text>
                            ) : null}
                          </Group>
                        </DataTd>
                        <DataTd>
                          <Group gap={6} wrap="nowrap" justify="flex-end">
                            <Button
                              size="compact-xs"
                              variant="subtle"
                              leftSection={<IconExternalLink size={14} stroke={1.5} />}
                              onClick={() => {
                                setTicketsModal({ open: false, loading: false, data: null })
                                openTicketRow(t, slug)
                              }}
                            >
                              Открыть
                            </Button>
                            <Button
                              size="compact-xs"
                              variant="light"
                              onClick={() => openTicketRowInBackground(t, slug)}
                            >
                              В фоне
                            </Button>
                          </Group>
                        </DataTd>
                      </DataTr>
                    )
                  })}
                </DataTbody>
              </DataTable>
            </ScrollArea.Autosize>
          </>
        ) : ticketsModal.data?.ticket_ids && ticketsModal.data.ticket_ids.length > 0 ? (
          <Stack gap="xs">
            <Text size="sm" c="dimmed">
              Только идентификаторы ({ticketsModal.data.ticket_ids.length}).
            </Text>
            {ticketsModal.data.ticket_ids.map((id) => (
              <Group key={id} justify="space-between" wrap="nowrap">
                <Text
                  size="sm"
                  style={{ cursor: 'pointer' }}
                  c="blue"
                  onClick={() => {
                    setTicketsModal({ open: false, loading: false, data: null })
                    openOrSelectTab(`/tickets/${encodeURIComponent(id)}`)
                  }}
                >
                  {id}
                </Text>
                <Button
                  size="compact-xs"
                  variant="light"
                  onClick={() => openNewTab(`/tickets/${encodeURIComponent(id)}`, { activate: false })}
                >
                  В фоне
                </Button>
              </Group>
            ))}
          </Stack>
        ) : (
          <Text size="sm" c="dimmed">
            Нет данных.
          </Text>
        )}
      </AppModal>
    </PageShell>
  )
}
